// ui/presenters/credentialingOptions.js
import { i18n } from 'boot/i18n';

import {
  CREDENTIALING_BODIES,
  bodyLevels,
  bodyName,
  isDeprecatedBody,
  numberLabelKeyFor,
} from '../../domain/constants/credentialingBodies';

/**
 * The credentialing vocabulary as a picker reads it.
 *
 * HERE RATHER THAN IN `domain/`, because every function below translates or orders for a screen, and
 * neither is a fact about credentialing. The constant itself is domain — which bodies exist, what
 * each issues, whether it expires — and it stays there, unaware that anything renders it. Keeping
 * these beside it would have the domain importing `boot/i18n`, which is the dependency pointing the
 * wrong way.
 */

/**
 * The bodies as options, ordered by name, with deprecated ones last and marked.
 *
 * Deprecated bodies are still offered rather than hidden: one is a body people have already recorded
 * against, and a picker that cannot name what a record holds makes that record uneditable. Sorting
 * it out of the way is what stops somebody reaching for it by accident.
 */
export const credentialingBodyOptions = () => {
  return Object.keys(CREDENTIALING_BODIES)
    .map((value) => ({
      value,
      label: isDeprecatedBody(value)
        ? i18n.t('staff.qualifications.labelDeprecatedBody', { name: bodyName(value) })
        : bodyName(value),
      isDeprecated: isDeprecatedBody(value),
    }))
    .sort((a, b) => {
      return a.isDeprecated === b.isDeprecated
        ? a.label.localeCompare(b.label)
        : Number(a.isDeprecated) - Number(b.isDeprecated);
    });
};

/** What a body issues, as options. Empty where the body issues exactly one thing. */
export const levelOptionsFor = (bodyId) => {
  return bodyLevels(bodyId).map((level) => ({ value: level, label: level }));
};

/**
 * What this body calls the number on its document.
 *
 * A state grants a license and a board grants a certificate; asking for a "certificate number" on a
 * license asks for something the document in front of the reader does not call itself.
 */
export const numberLabelFor = (bodyId) => {
  return i18n.t(`staff.qualifications.${numberLabelKeyFor(bodyId)}`);
};

/**
 * Every credential somebody can hold, flattened across the bodies that issue them.
 *
 * ONE LIST RATHER THAN A BODY PICKER AND THEN A LEVEL PICKER, because the question a payer's rule
 * asks is "does this person hold a BCBA", not "which board, and then what from it". Two dependent
 * selects would make the caller model a relationship it has no use for.
 *
 * DEPRECATED BODIES ARE ABSENT, unlike `credentialingBodyOptions` above, and the asymmetry is the
 * point: that list exists so a record already written against a body can still be edited, which is a
 * reason to keep offering it. This list is for authoring a NEW rule about the future, where a
 * deprecated body is an answer nobody should be able to choose. A body issuing nothing is absent for
 * the same reason, since a rule naming no level could not be evaluated.
 *
 * The body travels with each option because a level means nothing without its issuer, and the caller
 * needs it to say which — a reader seeing "LBA" and "BCBA" side by side cannot otherwise tell that
 * one is a state's and one is a board's.
 *
 * @returns {Array<{ organization: string, level: string, bodyName: string }>}
 */
export const qualifyingCredentialOptions = () => {
  return Object.keys(CREDENTIALING_BODIES)
    .filter((organization) => !isDeprecatedBody(organization))
    .flatMap((organization) => {
      return bodyLevels(organization).map((level) => ({
        organization,
        level,
        bodyName: bodyName(organization),
      }));
    });
};
